import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params} from '@angular/router';
import {Sort} from '@angular/material/sort';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, take, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {KeycloakService} from 'keycloak-angular';
import {UserDTO} from '../../../../clm/models/user/UserDTO';
import {TableControl} from '../../../../common/models/TableControl';
import {
  ConfirmationDialogComponent
} from '../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {AddUserDialogComponent} from '../../dialogs';
import {OtherRequestBuilder} from '../../../services/other-request-builder';
import {UserService} from '../../../services/user.service';
import {SharedService} from '../../../../common/services/shared-service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {Common, getSortDirection} from '../../../../common/util/Util';
import {DateUtil} from '../../../../common/util/DateUtil';
import {Role} from '../../../../clm/types/role.enum';
import {detailsExpand} from '../../../../common';
import {UserRoleDTO} from '../../../../clm/models/user/UserRoleDTO';
import {
  GenericIframeDialogComponent
} from 'src/app/modules/common/dialogs/generic-iframe-dialog/generic-iframe-dialog.component';
import {PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {PortalType} from '../../../../common/types/portal.enum';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';
import {FormControl} from '@angular/forms';


enum TableColumns {
  Icons = 'icons',
  UserId = 'userId',
  KeycloakId = 'keycloakId',
  FirstName = 'firstName',
  LastName = 'lastName',
  IsActive = 'isActive',
  EmailAddress = 'emailAddress',
}

enum RegistrationStatus {
  All = 'All',
  Approved = 'Approved',
  Pending = 'Pending'
}

@Component({
  selector: 'app-user-management-view',
  templateUrl: './user-management-view.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class UserManagementViewComponent implements OnInit, OnDestroy {
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly tableColumns = TableColumns;
  readonly registrationStatusEnum = RegistrationStatus;
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly userRolesChartLink: string = 'https://miro.com/app/live-embed/uXjVNbjp1no=/?autoplay=true&embedMode=view_only_without_ui&moveToViewport=1592,112,1577,1035';
  protected readonly Object = Object;

  hasPermissionToManageUsers: boolean;
  loggedInUserRoles: string[];
  isBusyCounter = 0;
  userDataSource: MatTableDataSource<UserDTO> = new MatTableDataSource();
  userDisplayedColumns: TableColumns[] = Object.values<TableColumns>(TableColumns);
  userExpandedRow: UserDTO | null;
  userTableControl: TableControl;
  userRoles: UserRoleDTO[];
  loggedInUserName: string;
  isProviderPortal = false;
  currentPortal: PortalType;
  registrationStatusFormControl: FormControl;
  textChanged$ = new Subject<string>();

  @ViewChild('userPaginator', {static: false}) userPaginator: MatPaginator;

  constructor(public dialog: MatDialog,
              private userService: UserService,
              private route: ActivatedRoute,
              private keycloakService: KeycloakService,
              private sharedService: SharedService,
              private userAuthorisationService: UserAuthorisationService) {

    const controls = OtherRequestBuilder.getTableControls();
    this.userTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      TableColumns.UserId,
      'DESCENDING',
      '',
      ''
    );

    // Note:
    // Checking Route static data that is passed to child routes
    const parentData = this.route.parent?.snapshot.data;
    this.isProviderPortal = parentData?.providerPortal;
    this.currentPortal = this.isProviderPortal ? PortalType.Provider : (parentData?.portalType || PortalType.Normal);
    this.registrationStatusFormControl = new FormControl(RegistrationStatus.Approved);
  }


  ngOnInit(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });

    this.loggedInUserRoles = this.keycloakService.getUserRoles();
    this.setRoleBasedAccess();
    this.initSubscriptions();
  }

  initSubscriptions(): void {
    this.getListOfRoles();
    this.getListOfUsers(this.userTableControl);

    if (this.hasPermissionToManageUsers) {
      this.route.queryParams
        .subscribe((params: Params) => {
          if (params.dialog) {
            this.openUserDialog();
          }
        });

      this.textChanged$.pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        debounceTime(this.debounceTime)
      )
        .subscribe(
          (value: string) => {
            this.getListOfUsers(this.userTableControl);
          },
          error => console.log(error));
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  isCurrentLoggedInUser(user: UserDTO): boolean {
    return `${user.firstName} ${user.lastName}` === this.loggedInUserName;
  }

  setRoleBasedAccess(): void {
    this.hasPermissionToManageUsers = this.userAuthorisationService.hasUserAnyManagementRole();
    this.filterDisplayedColumn();
  }

  onClickOpenUserRolesChart(): void {
    this.dialog.open(GenericIframeDialogComponent, {
      height: '90vh',
      width: '90vw',
      data: {heading: 'User Roles Chart', src: this.userRolesChartLink}
    });
  }

  onValueChanged(value: string): void {
    this.userTableControl.filterField = TableColumns.LastName;
    this.userTableControl.filterValue = value;
    this.textChanged$.next(value);
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.userPaginator != null) {
      this.userPaginator.firstPage();
    }
    this.userTableControl.pageNumber = 0;
    this.userTableControl.sortField = $event.active;
    this.userTableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfUsers(this.userTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.userTableControl.pageNumber = $event.pageIndex;
    this.userTableControl.pageSize = $event.pageSize;
    this.getListOfUsers(this.userTableControl);
  }

  onClickEditUser(event: MouseEvent, user: UserDTO): void {
    event.preventDefault();
    event.stopPropagation();
    this.openUserDialog(user);
  }

  onClickDeleteUser(event: MouseEvent, user: UserDTO): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isCurrentLoggedInUser(user)) {
      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this ${user.keycloakId} user?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          const {userId} = user;
          this.onDeleteUserConfirmed(userId);
        }
      });
  }

  onClickResetUserPassword(event: MouseEvent, user: UserDTO): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isCurrentLoggedInUser(user)) {
      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open(ConfirmationDialogComponent, {
        width: '40vw',
        data: `Are you sure you wish to reset the password for ${user.keycloakId}?`
      }
    );

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.onResetUserPasswordConfirmed(user);
        }
      });
  }

  onResetUserPasswordConfirmed(userDTO: UserDTO): void {
    this.userService.resetUserPassword(userDTO)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (_) => this.sharedService.openSnackBar(`The password has been reset and a reset link has been emailed to the user`, 'Dismiss', 30000),
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  onDeleteUserConfirmed(userId: bigint): void {
    this.userService.deleteUserBy(userId)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (_) => {
          this.sharedService.openSnackBar(`User ${userId} deleted`, 'Dismiss');
          this.getListOfUsers(this.userTableControl);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  openUserDialog(user?: UserDTO): void {
    const rolesAccessibleByLoggedInUser = this.getAccessibleRoles();
    const dialogRef: MatDialogRef<AddUserDialogComponent> = this.dialog.open(AddUserDialogComponent, {
      width: '50vw',
      data: {
        userId: user?.userId,
        selectedUser: user,
        userRoles: rolesAccessibleByLoggedInUser,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result?.refetch) {
            this.getListOfUsers(this.userTableControl);
          }
        },
        error: (error) => this.sharedService.openErrorDialog(error.message)
      });
  }

  isEditIconVisible(user: UserDTO): boolean {
    return this.hasPermissionToManageUsers && user.requiresApproval != 1;
  }

  isResetPasswordIconVisible(user: UserDTO): boolean {
    return this.hasPermissionToManageUsers && user.requiresApproval != 1;
  }

  isDeleteIconVisible(user: UserDTO): boolean {
    if (this.userAuthorisationService.hasRoleAdmin()) {
      return true;
    } else if (this.hasPermissionToManageUsers && user.requiresApproval === 1) {
      return true;
    } else {
      return false;
    }
  }

  isApproveIconVisible(user: UserDTO): boolean {
    return this.hasPermissionToManageUsers && user.requiresApproval === 1;
  }

  onRegistrationStatusSelectionChanged(value: string) {
    this.registrationStatusFormControl.setValue(value);
    this.getListOfUsers(this.userTableControl);
  }

  private getListOfRoles(): void {
    this.isBusyCounter++;
    this.userService.getRoles()
      .pipe(
        take(1),
        finalize(() => this.isBusyCounter--)
      )
      .subscribe({
        next: (roles: UserRoleDTO[]) => this.userRoles = roles?.length ? roles : [],
        error: (error) => console.log(error)
      });

  }

  private getListOfUsers(tableControl: TableControl): void {
    const pagedRequestDTO = this.getFieldMapFilters(tableControl);

    this.isBusyCounter++;
    this.userService
      .getListOfUsers(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        finalize(() => {
          this.isBusyCounter--;
        })
      )
      .subscribe({
        next: (response: PagedResponseDTO<UserDTO>) => {
          this.userDataSource.data = response.resultList;
          this.userTableControl.length = response.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      });
  }

  private getFieldMapFilters(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO: PagedRequestDTO = OtherRequestBuilder.getTblUserPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    if (tableControl != null && tableControl.filterValue?.trim() !== '') {
      const value: string = tableControl.filterValue?.trim();

      const firstName: PagedFilterDTONew = new PagedFilterDTONew();
      firstName.name = 'firstName';
      firstName.value = value;
      firstName.logicGate = 'AND';
      firstName.bracket = '(';
      pagedRequestDTO.fieldMap[`firstName`] = firstName;

      const lastName: PagedFilterDTONew = new PagedFilterDTONew();
      lastName.name = 'lastName';
      lastName.value = value;
      lastName.logicGate = 'OR';
      lastName.bracket = '';
      pagedRequestDTO.fieldMap[`lastName`] = lastName;

      const keycloakId: PagedFilterDTONew = new PagedFilterDTONew();
      keycloakId.name = 'keycloakId';
      keycloakId.value = value;
      keycloakId.logicGate = 'OR';
      keycloakId.bracket = ')';
      pagedRequestDTO.fieldMap[`keycloakId`] = keycloakId;
    }

    if (this.registrationStatusFormControl.value !== RegistrationStatus.All) {
      const registrationStatus: PagedFilterDTONew = new PagedFilterDTONew();
      registrationStatus.name = 'requiresApproval';
      registrationStatus.value = this.registrationStatusFormControl.value === RegistrationStatus.Pending ? '1' : '0';
      pagedRequestDTO.fieldMap[`requiresApproval`] = registrationStatus;
    }

    if (this.mustFilterVisibleUsersPerRole()) {
      const isVisiblePagedFilterDTO: PagedFilterDTONew = new PagedFilterDTONew();
      isVisiblePagedFilterDTO.name = 'isVisible';
      isVisiblePagedFilterDTO.value = this.showUserPerRole();
      pagedRequestDTO.fieldMap[`isVisible`] = isVisiblePagedFilterDTO;
    }

    return pagedRequestDTO;
  }

  private mustFilterVisibleUsersPerRole(): boolean {
    let filterVisibleUsers = true;
    if (this.userAuthorisationService.hasRoleInternalUserManager() && this.userAuthorisationService.hasRoleExternalUserManager()) {
      filterVisibleUsers = false;
    }
    return filterVisibleUsers;
  }

  private showUserPerRole(): string {
    let usersVisible = '-1';
    if (this.userAuthorisationService.hasRoleBrokerUserManager()) {
      usersVisible = '0';
    } else if (this.userAuthorisationService.hasRoleProviderUserManager()) {
      usersVisible = '0';
    } else if (this.userAuthorisationService.hasRoleInternalUserManager()) {
      usersVisible = '1';
    }
    return usersVisible;
  }

  private filterDisplayedColumn(): void {
    if (!this.hasPermissionToManageUsers) {
      this.userDisplayedColumns = this.userDisplayedColumns.filter((key: TableColumns) => key !== TableColumns.Icons);
    }
  }

  private getAccessibleRoles(): UserRoleDTO[] {
    const isInternalUserManager  = this.userAuthorisationService.hasRoleInternalUserManager();
    const isBrokerUserManager    = this.userAuthorisationService.hasRoleBrokerUserManager();
    const isProviderUserManager  = this.userAuthorisationService.hasRoleProviderUserManager();
    const isOnboardingPortal     = this.currentPortal === PortalType.OnBoarding;

    const rolesAccessibleByLoggedInUser = this.userRoles
      .filter(role => role.role_Name !== Role.Default)
      .filter(role => {
        // 1. Only show broker role to broker user manager
        if (role.role_Name === Role.Broker) { return isBrokerUserManager; }
        // 1. Only show provider role to provider user manager
        if (role.role_Name === Role.Provider) { return isProviderUserManager; }

        // 3. Do not show other internal roles if not internal user manager
        if (!isInternalUserManager) { return false; }

        // 4. Only show onboarding roles, broker user manager and user manager to onboarding user manager
        if (isOnboardingPortal && isInternalUserManager) {
          return role.role_Name.startsWith('onboarding') || role.role_Name === Role.BrokerUserManager || role.role_Name === Role.UserManager;
        }

        // 5. Show all other roles to internal user manager on normal portal
        return true;
      }
    );

    return rolesAccessibleByLoggedInUser;
  }

}
